import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useAuth } from "../../context/AuthContext";
import { useQuotes } from "../../context/QuoteContext";
import { useLargeFormatFormLogic } from "../../hooks/useLargeFormatFormLogic";
import UniversalPieceManager from "../../components/large-format/UniversalPieceManager";
import QuoteReview from "./QuoteReview";
import ManagerQuoteReview from "../../components/manager/ManagerQuoteReview";
import PrintlogicSuccessModal from "../../components/common/PrintlogicSuccessModal";
import DetailedPriceBreakdown from "../../components/large-format/DetailedPriceBreakdown";
import SimplePriceBreakdown from "../../components/large-format/SimplePriceBreakdown";
import CustomPricingBanner from "../../components/common/CustomPricingBanner";
import { validateLargeFormatQuote } from "../../components/common/quoteLoadingUtils";
import { getLargeFormatProcessNames } from "../../utils/largeFormatFinishing";

const API_URL = process.env.REACT_APP_API_URL || "";

const JOB_TYPE_MATERIAL_MAP = {
  banner: ["banner"],
  "mounted-boards": ["vinyl", "board", "laminate"],
  boards: ["board", "laminate"],
  posters: ["poster"],
  fabric: ["fabric"],
  "vinyl-stickers": ["vinyl", "laminate"],
  "print-cut": ["vinyl", "laminate"],
  cutvinyl: ["cutvinyl"], // cutvinyl collection already includes both vinyl and colouredvinyl
  "roller-banner": ["rollerbanner"],
  dreams: ["board"], // Dreams: board only (vinyl auto-selected but hidden)
  default: [
    "vinyl",
    "board",
    "banner",
    "poster",
    "laminate",
    "rollerbanner",
    "colouredvinyl",
    "cutvinyl",
  ],
};

const JOB_TYPE_FINISHING_MAP = {
  banner: ["hem-and-eyelet", "pole-pocket"],
  "mounted-boards": [
    "router-subject-to-material-multiplier",
    "mounting-to-board",
    "mounting-to-board-double-sided",
    "guillotining",
    "adding-sign-channel",
    "laminate",
    "velcro",
    "double-sided-tape",
    "post-clips",
  ],
  boards: [
    "router-subject-to-material-multiplier",
    "guillotining",
    "adding-sign-channel",
    "laminate",
  ],
  posters: ["guillotining"],
  fabric: ["guillotining"],
  "vinyl-stickers": [
    "laminate",
    "weeding-simple-stickers",
    "kiss-cut",
    "guillotining",
  ],
  cutvinyl: [
    "cutvinyl",
    "weeding+application-tape",
    "weeding",
    "cut-vinyl-(no-cut-path)",
  ],
  "roller-banner": [],
  "print-cut": [
    "laminate",
    "weeding-simple-stickers",
    "kiss-cut",
    "guillotining",
  ],
  dreams: ["ds-tape", "magnetic-tape"], // Dreams: only these two finishes
  default: [
    "hem-and-eyelet",
    "laminate",
    "kiss-cut",
    "mounting-to-board",
    "mounting-to-board-(double-sided)",
    "router-subject-to-material-multiplier",
    "guillotining",
    "weeding",
    "adding-sign-channel",
  ],
};

const MATERIAL_ALLOWED_JOB_TYPES = {
  "ferrous-vinyl": ["mounted-boards"],
  "self-cling-vinyl": ["vinyl-stickers"],

  // Future material restrictions can be added here
};

const MATERIAL_DISALLOWED_FINISHES = {
  "self-cling-vinyl": ["laminate"],

  // Future material-specific finish restrictions can be added here
};

const weedingComplexityMultipliers = {
  easy: 1.0,
  medium: 2.0,
  hard: 4.0,
};

const resolveWeedingLevelFromMultiplier = (value) => {
  if (value == null) {
    return "easy";
  }
  const numeric = Number(value);
  const matched = Object.entries(weedingComplexityMultipliers).find(
    ([, multiplier]) => Math.abs(multiplier - numeric) < 0.0001,
  );
  return matched ? matched[0] : "easy";
};

const safeRandomUUID = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `uuid_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

const generateTempId = (prefix = "item") =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

const roundCurrency = (value) =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100;

const PriceCalculator = ({
  pricingTier,
  selectedCustomer = null,
  onResetCustomer = null,
  // Dialog mode props
  isDialogMode = false,
  onSaveEmailQuote = null,
  dialogBridge = null,
  initialEmailQuote = null,
  onCatalogLoaded = null,
}) => {
  const catalogNotifiedRef = useRef(false);
  const {
    quoteToLoad,
    targetCalculator,
    clearLoadedQuote,
    addDraft,
    updateDraft,
    editingDraftId,
    stopEditingDraft,
    addProductsToQuote,
    currentQuote,
  } = useQuotes();
  const { currentUser, hasRole } = useAuth();
  const showDetailedBreakdown = currentUser?.show_detailed_breakdown || false;

  const {
    universalPieces,
    setUniversalPieces,
    machineId,
    setMachineId,
    tradeDiscount,
    setTradeDiscount,
    notes,
    setNotes,
    jobName,
    setJobName,

    // Data
    jobTypes,
    // Data collections used in rendering but not explicitly destructured in old code?
    // Old code used: vinylMaterials, boardMaterials, etc. I need to destructure them all or just rely on them being available if I pass them.
    vinylMaterials,
    boardMaterials,
    bannerMaterials,
    posterMaterials,
    laminateMaterials,
    rollerBannerMaterials,
    colouredVinylMaterials,
    cutVinylMaterials,
    fabricMaterials,
    finishingProcesses,
    printMachines,
    finishingMaterials,

    // Results
    calculatedPrice,
    setCalculatedPrice,
    breakdown,
    setBreakdown,
    error,
    setError,
    isLoading,
    isLoadingQuote,
    setQuoteLoadWarnings,

    // Helpers
    getMaterialsForJobType,
    getFinishesForJobType,
    allowMultipleMaterialsForJobType,
    sanitizeUniversalPiecesForApi,
    quoteLoadWarnings,
    autoExpandKey,
    loadQuote: loadQuoteIntoCalculator,
    findMaterialById,
  } = useLargeFormatFormLogic({ pricingTier, selectedCustomer });

  // Email quote mode state (for dialog mode)
  const [isEmailQuoteMode, setIsEmailQuoteMode] = useState(false);
  const [originalEmailQuote, setOriginalEmailQuote] = useState(null);

  const [showQuoteReview, setShowQuoteReview] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState({
    orderNumber: "",
    quoteName: "",
  });
  const [productAddSuccess, setProductAddSuccess] = useState(false);
  const [productAddSuccessCount, setProductAddSuccessCount] = useState(0);

  // NOTE: removed fetchData() as it is handled in the hook

  // Redundant materialCollections memo removed as hook provides structured materials

  // Email quote loading for dialog mode
  useEffect(() => {
    // Dialog sessions always use email-quote mode UX (even blank) so we always send back to Outlook
    if (isDialogMode && !isEmailQuoteMode) {
      setIsEmailQuoteMode(true);
    }

    if (initialEmailQuote && isDialogMode && !isLoading) {
      const dataLoaded =
        jobTypes.length > 0 &&
        vinylMaterials.length > 0 &&
        finishingProcesses.length > 0 &&
        printMachines.length > 0;

      if (dataLoaded) {
        console.log(
          "Loading email quote in large format dialog mode:",
          initialEmailQuote,
        );
        setOriginalEmailQuote(initialEmailQuote);
        loadQuoteIntoCalculator(initialEmailQuote);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialEmailQuote,
    isDialogMode,
    isEmailQuoteMode,
    isLoading,
    jobTypes,
    vinylMaterials,
    finishingProcesses,
    printMachines,
    // loadQuoteIntoCalculator is stable (wrapped in useCallback), so we omit it
  ]);

  useEffect(() => {
    if (!isDialogMode || typeof onCatalogLoaded !== "function") {
      return;
    }
    if (catalogNotifiedRef.current) {
      return;
    }
    if (isLoading) {
      return;
    }

    onCatalogLoaded({
      jobTypes,
      finishingProcesses,
      printMachines,
      vinylMaterials,
      boardMaterials,
      bannerMaterials,
      posterMaterials,
      laminateMaterials,
      rollerBannerMaterials,
      colouredVinylMaterials,
      cutVinylMaterials,
      fabricMaterials,
      error,
    });

    if (
      !jobTypes.length ||
      !finishingProcesses.length ||
      !printMachines.length
    ) {
      console.warn("Large format catalogue loaded with missing datasets", {
        jobTypes: jobTypes.length,
        finishingProcesses: finishingProcesses.length,
        printMachines: printMachines.length,
        error,
      });
    }

    catalogNotifiedRef.current = true;
  }, [
    isDialogMode,
    onCatalogLoaded,
    isLoading,
    jobTypes,
    finishingProcesses,
    printMachines,
    vinylMaterials,
    boardMaterials,
    bannerMaterials,
    posterMaterials,
    laminateMaterials,
    rollerBannerMaterials,
    colouredVinylMaterials,
    cutVinylMaterials,
    fabricMaterials,
    error,
  ]);

  // Helper functions removed as they are provided by useLargeFormatFormLogic hook

  // sanitizeUniversalPiecesForApi removed (provided by hook)

  const summarizeMultiPieceProduct = useCallback(
    (pieces) => {
      const totalPieces = pieces.length;
      const totalItems = pieces.reduce((sum, piece) => sum + piece.quantity, 0);
      const uniqueJobTypeIds = [
        ...new Set(pieces.map((piece) => piece.jobType).filter(Boolean)),
      ];
      const jobTypeNames = uniqueJobTypeIds.map(
        (id) => jobTypes.find((jt) => jt.id === id)?.name || id,
      );
      const jobTypeLabel =
        jobTypeNames.length === 1 ? jobTypeNames[0] : "Mixed Large Format";

      const materialNames = [
        ...new Set(
          pieces.flatMap((piece) =>
            piece.materials.map((material) => {
              const match = findMaterialById(material.id, material.type);
              return match?.name || material.name || material.id;
            }),
          ),
        ),
      ];

      const processNames = [
        ...new Set(
          pieces
            .flatMap((piece) => piece.finishes || [])
            .map(
              (finishId) =>
                finishingProcesses.find((process) => process.id === finishId)
                  ?.name,
            )
            .filter(Boolean),
        ),
      ];

      return {
        totalPieces,
        totalItems,
        jobTypeNames,
        jobTypeLabel,
        materialNames,
        processNames,
      };
    },
    [jobTypes, findMaterialById, finishingProcesses],
  );

  const buildProductsFromPieces = useCallback(
    (validPieces, summary) => {
      if (!Array.isArray(validPieces) || validPieces.length === 0) {
        return [];
      }

      const groupId = safeRandomUUID();
      const groupCount = validPieces.length;
      const resolvedMachineId = machineId || validPieces[0]?.machine_id || "";
      const machineName =
        printMachines.find((machine) => machine.id === resolvedMachineId)
          ?.name || "Unknown Machine";

      const pricingTierUsed =
        breakdown?.pricing_tier_final_result ||
        breakdown?.pricing_tier ||
        pricingTier ||
        "trade";

      const partnerDetails = breakdown?.partner_tier_details || {};
      const defaultPartnerDiscountPercent =
        partnerDetails?.default_discount_percent ??
        partnerDetails?.discount_percent ??
        breakdown?.default_partner_discount_percent ??
        37.5;

      const finalTotal =
        typeof breakdown?.total_cost === "number"
          ? breakdown.total_cost
          : calculatedPrice;
      const piecesBreakdown = Array.isArray(breakdown?.pieces)
        ? breakdown.pieces
        : [];
      const defaultGroupName =
        (jobName && jobName.trim()) ||
        summary?.jobTypeLabel ||
        "Large Format Multi-piece";

      const pieceData = validPieces.map((piece, index) => {
        const pieceBreakdown = { ...(piecesBreakdown[index] || {}) };
        const piecePricingTier =
          pieceBreakdown.pricing_tier_final_result ||
          pieceBreakdown.pricing_tier ||
          pricingTierUsed;

        const retailSubtotal =
          typeof pieceBreakdown.retail_subtotal_before_min_charge === "number"
            ? pieceBreakdown.retail_subtotal_before_min_charge
            : typeof pieceBreakdown.sub_total_before_min_charge === "number"
              ? pieceBreakdown.sub_total_before_min_charge
              : 0;

        let basePrice = 0;
        if (typeof pieceBreakdown.total_cost_before_min_charge === "number") {
          basePrice = pieceBreakdown.total_cost_before_min_charge;
        } else if (typeof pieceBreakdown.total_cost === "number") {
          basePrice = pieceBreakdown.total_cost;
        } else {
          basePrice = retailSubtotal;
        }

        if (
          piecePricingTier === "partner" &&
          !pieceBreakdown.partner_pricing_note
        ) {
          if (
            typeof pieceBreakdown.partner_subtotal_before_min_charge ===
            "number"
          ) {
            basePrice = pieceBreakdown.partner_subtotal_before_min_charge;
          } else {
            const partnerDelta =
              typeof pieceBreakdown.partner_delta_before_min_charge === "number"
                ? pieceBreakdown.partner_delta_before_min_charge
                : 0;
            basePrice =
              retailSubtotal * (1 - defaultPartnerDiscountPercent / 100) +
              partnerDelta;
          }
        }

        const resolvedMaterials = Array.isArray(piece.materials)
          ? piece.materials
              .map((material) => {
                const materialType =
                  material.type || findMaterialById(material.id)?.type;
                const materialData =
                  findMaterialById(material.id, materialType) || material;
                if (!materialData?.id) {
                  return null;
                }
                return {
                  id: materialData.id,
                  type: materialType || materialData.type || material.type,
                  name: materialData.name || material.name || materialData.id,
                };
              })
              .filter(Boolean)
          : [];

        const materialNames = resolvedMaterials
          .map((material) => material.name)
          .join(", ");

        const finishNames = getLargeFormatProcessNames(
          { ...piece, breakdown: pieceBreakdown },
          finishingProcesses,
        );

        const pieceJobType =
          jobTypes.find((jobType) => jobType.id === piece.jobType) || {};
        const jobTypeLabel =
          pieceJobType.name || pieceBreakdown.job_type || "Large Format Item";

        const widthValue = Number.isFinite(piece.width)
          ? piece.width
          : pieceBreakdown.width_mm;
        const heightValue = Number.isFinite(piece.height)
          ? piece.height
          : pieceBreakdown.height_mm;
        const dimensionLabel =
          widthValue && heightValue
            ? `${widthValue}mm × ${heightValue}mm`
            : pieceBreakdown.dimensions || "";

        const resolvedName =
          (piece.name || "").trim() || jobTypeLabel || "Large Format Item";
        const quantity = Number.isFinite(piece.quantity)
          ? Number(piece.quantity)
          : Number(pieceBreakdown.quantity) || 1;

        return {
          piece,
          pieceBreakdown,
          basePrice: roundCurrency(basePrice),
          materialNames,
          materials: resolvedMaterials,
          finishNames,
          jobTypeLabel,
          dimensionLabel,
          resolvedName,
          quantity,
        };
      });

      const baseTotal = pieceData.reduce(
        (sum, item) => sum + item.basePrice,
        0,
      );
      const effectiveFinalTotal = roundCurrency(finalTotal);
      const totalAdjustment = roundCurrency(effectiveFinalTotal - baseTotal);

      const products = pieceData.map((item, index) => {
        const proportionalAdjustment =
          baseTotal > 0
            ? (item.basePrice / baseTotal) * totalAdjustment
            : totalAdjustment / groupCount;
        const adjustmentRounded = roundCurrency(proportionalAdjustment);
        const finalPrice = roundCurrency(item.basePrice + adjustmentRounded);

        const breakdownWithAllocation = {
          ...item.pieceBreakdown,
          multi_piece_allocation: {
            group_id: groupId,
            index: index + 1,
            count: groupCount,
            base_price_before_adjustment: item.basePrice,
            allocated_adjustment: adjustmentRounded,
            final_price: finalPrice,
            pricing_tier: pricingTierUsed,
          },
        };

        const resolvedMachineIdForPiece =
          item.piece.machine_id || resolvedMachineId;
        const machineLabel =
          printMachines.find(
            (machine) => machine.id === resolvedMachineIdForPiece,
          )?.name || machineName;

        return {
          id: generateTempId("item"),
          jobName: item.resolvedName,
          productType: "large-format",
          materialNames: item.materialNames,
          material: item.materialNames,
          materials: item.materials,
          dimensions:
            item.dimensionLabel ||
            `${item.quantity} item${item.quantity === 1 ? "" : "s"}`,
          quantity: item.quantity,
          processes: item.finishNames,
          price: finalPrice,
          breakdown: breakdownWithAllocation,
          width: Number.isFinite(item.piece.width)
            ? Number(item.piece.width)
            : undefined,
          height: Number.isFinite(item.piece.height)
            ? Number(item.piece.height)
            : undefined,
          finishes: Array.isArray(item.piece.finishes)
            ? [...item.piece.finishes]
            : [],
          machineId: resolvedMachineIdForPiece,
          machine: machineLabel,
          jobType: item.jobTypeLabel,
          tradeDiscount,
          notes,
          calculatorMode: "multi",
          addedAt: new Date().toISOString(),
          multiPieceGroupId: groupId,
          multiPieceGroupName: defaultGroupName,
          multiPieceGroupIndex: index + 1,
          multiPieceGroupCount: groupCount,
          multiPieceBasePrice: item.basePrice,
          multiPieceAdjustment: adjustmentRounded,
          multiPiecePieceId:
            item.piece.clientId || item.piece.id || generateTempId("piece"),
          multiPiecePricingTier: pricingTierUsed,
          pieceConfiguration: { ...item.piece },
        };
      });

      if (products.length > 0) {
        products[0].multiPieceGroupSummary = {
          groupId,
          groupName: defaultGroupName,
          finalTotal: effectiveFinalTotal,
          baseTotal: roundCurrency(baseTotal),
          adjustment: totalAdjustment,
          pricingTier: pricingTierUsed,
          totalPieces: groupCount,
          totalItems: summary?.totalItems ?? undefined,
          jobTypeLabel: summary?.jobTypeLabel,
          materialNames: summary?.materialNames || [],
          processNames: summary?.processNames || [],
        };
      }

      return products;
    },
    [
      breakdown,
      calculatedPrice,
      findMaterialById,
      finishingProcesses,
      jobName,
      jobTypes,
      machineId,
      notes,
      pricingTier,
      printMachines,
      tradeDiscount,
    ],
  );

  // loadQuoteIntoCalculator removed (provided by hook)

  // calculatePrice removed (provided by hook)

  // Debounced effect for calculation also removed as it is in the hook

  useEffect(() => {
    if (!quoteToLoad || targetCalculator !== "large-format" || isLoading) {
      return;
    }
    loadQuoteIntoCalculator(quoteToLoad);
    clearLoadedQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quoteToLoad,
    targetCalculator,
    clearLoadedQuote,
    isLoading,
    // loadQuoteIntoCalculator is stable (wrapped in useCallback), so we omit it
  ]);

  const resetForm = useCallback(
    (options = {}) => {
      const { preserveSuccessMessage = false, preserveCustomer = false } =
        options;

      setUniversalPieces([]);
      setTradeDiscount(false);
      setJobName("");
      setNotes("");
      setCalculatedPrice(0);
      setBreakdown({});
      setError(null);
      if (!preserveSuccessMessage) {
        setProductAddSuccess(false);
      }

      // Clear selected customer when resetting (unless preserveCustomer option is true)
      if (
        !preserveCustomer &&
        onResetCustomer &&
        typeof onResetCustomer === "function"
      ) {
        onResetCustomer();
      }

      if (printMachines && printMachines.length > 0) {
        setMachineId(printMachines[0].id);
      } else {
        setMachineId("");
      }
    },
    [printMachines, onResetCustomer],
  );

  const handleAddToQuote = useCallback(() => {
    if (calculatedPrice <= 0 && !error) {
      alert("Please configure your print job before adding to quote.");
      return;
    }

    if (error) {
      alert(`Cannot add to quote due to calculation error: ${error}`);
      return;
    }

    const { validPieces, errors } = sanitizeUniversalPiecesForApi({
      strict: true,
    });

    if (errors.length > 0) {
      alert(errors[0]);
      return;
    }

    if (validPieces.length === 0) {
      alert(
        "Please add at least one complete piece before adding to the quote.",
      );
      return;
    }

    const summary = summarizeMultiPieceProduct(validPieces);
    const products = buildProductsFromPieces(validPieces, summary);

    if (!products.length) {
      alert(
        "Unable to create products for this configuration. Please review the pieces and try again.",
      );
      return;
    }

    addProductsToQuote(products);
    setProductAddSuccessCount(products.length);
    setProductAddSuccess(true);
    setTimeout(() => {
      setProductAddSuccess(false);
      setProductAddSuccessCount(0);
    }, 3000);
    resetForm({ preserveSuccessMessage: true, preserveCustomer: true });
  }, [
    calculatedPrice,
    error,
    sanitizeUniversalPiecesForApi,
    summarizeMultiPieceProduct,
    buildProductsFromPieces,
    addProductsToQuote,
    resetForm,
  ]);

  const createProductsForReview = useCallback(() => {
    if (calculatedPrice <= 0 || error) return [];

    const { validPieces, errors } = sanitizeUniversalPiecesForApi({
      strict: true,
    });
    if (errors.length > 0 || validPieces.length === 0) {
      return [];
    }

    const summary = summarizeMultiPieceProduct(validPieces);
    return buildProductsFromPieces(validPieces, summary);
  }, [
    calculatedPrice,
    error,
    sanitizeUniversalPiecesForApi,
    summarizeMultiPieceProduct,
    buildProductsFromPieces,
  ]);

  const openQuoteReview = useCallback(() => {
    if (currentQuote.items.length === 0 && (calculatedPrice <= 0 || !!error)) {
      alert(
        "Please configure at least one product before reviewing the quote.",
      );
      return;
    }

    if (calculatedPrice > 0 && !error) {
      const products = createProductsForReview();
      if (products.length) {
        addProductsToQuote(products);
        resetForm({ preserveSuccessMessage: false, preserveCustomer: true });
      }
    }

    setShowQuoteReview(true);
  }, [
    currentQuote.items.length,
    calculatedPrice,
    error,
    createProductsForReview,
    addProductsToQuote,
    resetForm,
  ]);

  const buildDraftData = useCallback(() => {
    const { validPieces, errors } = sanitizeUniversalPiecesForApi({
      strict: true,
    });
    if (errors.length > 0 || validPieces.length === 0) {
      return null;
    }

    const summary = summarizeMultiPieceProduct(validPieces);
    const pluralPieces = summary.totalPieces === 1 ? "" : "s";
    // The visible item-name editor updates the piece, not the legacy jobName.
    const draftName =
      (validPieces.length === 1 ? validPieces[0].name?.trim() : "") ||
      jobName?.trim() ||
      `${summary.jobTypeLabel} Draft ${new Date().toLocaleDateString()}`;
    const machineName =
      printMachines.find((m) => m.id === machineId)?.name || "Unknown Machine";

    // For multi-piece jobs, convert pieces into separate items (like quotes do)
    // Get per-piece breakdown from the multi-piece breakdown
    const piecesBreakdown = Array.isArray(breakdown?.pieces)
      ? breakdown.pieces
      : [];

    const finalTotal = roundCurrency(
      typeof breakdown?.total_cost === "number" ? breakdown.total_cost : calculatedPrice
    );
    const groupCount = validPieces.length;

    // Compute per-piece base prices using the same logic as buildProductsFromPieces
    const pieceBaseData = validPieces.map((piece, index) => {
      const pieceBreakdown = piecesBreakdown[index] || {};
      let basePrice = 0;
      if (typeof pieceBreakdown.total_cost_before_min_charge === "number") {
        basePrice = pieceBreakdown.total_cost_before_min_charge;
      } else if (typeof pieceBreakdown.total_cost === "number") {
        basePrice = pieceBreakdown.total_cost;
      }
      return { piece, pieceBreakdown, basePrice: roundCurrency(basePrice) };
    });

    const baseTotal = pieceBaseData.reduce((sum, d) => sum + d.basePrice, 0);
    const totalAdjustment = roundCurrency(finalTotal - baseTotal);

    const items = pieceBaseData.map(({ piece, pieceBreakdown, basePrice }, index) => {
      const proportionalAdjustment = baseTotal > 0
        ? roundCurrency((basePrice / baseTotal) * totalAdjustment)
        : roundCurrency(totalAdjustment / groupCount);
      const finalPrice = roundCurrency(basePrice + proportionalAdjustment);

      return {
        id: piece.clientId || `piece_${index}`,
        jobName: piece.name || piece.jobType || "Unnamed Piece",
        productType: "large-format",
        materialNames: piece.materialNames || piece.jobType,
        material: piece.materialNames || piece.jobType,
        materials: piece.materials || [],
        dimensions: piece.dimensions,
        quantity: piece.quantity,
        processes: piece.finishingProcesses || [],
        price: finalPrice,
        breakdown: pieceBreakdown,
        width: piece.width || 0,
        height: piece.height || 0,
        jobType: piece.jobType,
        finishes: piece.finishes || [],
        finishingOptions: piece.finishingOptions || {},
      };
    });

    return {
      // Use quote format for drafts (with items array) - matches how quotes are structured
      items,
      totalPrice: calculatedPrice,

      // Pieces in API format for DialogRouter's calculator_payload
      pieces: validPieces.map((piece) => ({
        width: piece.width,
        height: piece.height,
        quantity: piece.quantity,
        materials: piece.materials,
        finishes: piece.finishes,
        finishingOptions: piece.finishingOptions,
        jobType: piece.jobType,
        name: piece.name,
        machine_id: piece.machineId,
        router_cut_length: piece.routerCutLength,
        weeding_complexity: piece.weedingComplexity,
      })),

      // Full breakdown from calculator
      breakdown: breakdown,

      // Metadata for display
      draftName,
      quoteName: draftName,

      // Legacy fields for backward compatibility
      calculatorType: "large-format",
      calculatorMode: "multi",
      productType: "large-format",

      // Summary information
      jobType: summary.jobTypeLabel,
      materialNames: summary.materialNames.join(", "),
      dimensions: `${summary.totalPieces} piece${pluralPieces} (${summary.totalItems} total items)`,
      quantity: summary.totalItems,

      // Configuration
      machineId,
      machine: machineName,
      tradeDiscount,
      notes,

      createdAt: new Date().toISOString(),
    };
  }, [
    sanitizeUniversalPiecesForApi,
    summarizeMultiPieceProduct,
    printMachines,
    machineId,
    tradeDiscount,
    notes,
    calculatedPrice,
    breakdown,
    jobName,
  ]);

  const handleSaveDraft = useCallback(async () => {
    const draftData = buildDraftData();
    if (!draftData) {
      alert("Please complete your multi-piece configuration before saving.");
      return;
    }

    const savedDraft = await addDraft(draftData);
    if (savedDraft) {
      alert(editingDraftId ? "Saved as a new draft." : "Draft saved successfully!");
    } else {
      alert("Failed to save draft. Please try again.");
    }
  }, [
    buildDraftData,
    editingDraftId,
    addDraft,
  ]);

  const handleUpdateDraft = useCallback(async () => {
    if (!editingDraftId) {
      await handleSaveDraft();
      return;
    }

    const draftData = buildDraftData();
    if (!draftData) {
      alert("Please complete your multi-piece configuration before updating.");
      return;
    }

    const result = await updateDraft(editingDraftId, draftData);
    if (result?.success) {
      alert("Draft updated successfully!");
      stopEditingDraft();
      resetForm();
    } else {
      alert(`Failed to update draft. ${result?.message || ""}`);
    }
  }, [
    editingDraftId,
    handleSaveDraft,
    buildDraftData,
    updateDraft,
    stopEditingDraft,
    resetForm,
  ]);

  // Dialog mode: Save email quote with updated pricing
  const handleSaveEmailQuote = useCallback(async () => {
    // In dialog mode we always send back to Outlook via provided handler
    if (isDialogMode && onSaveEmailQuote) {
      try {
        const draftData = buildDraftData();
        if (!draftData) {
          alert("Please complete your configuration before saving.");
          return;
        }

        await onSaveEmailQuote(draftData);
        return; // Dialog will close via parent
      } catch (error) {
        console.error("Error saving email quote:", error);
        alert("Failed to save updated quote. Please try again.");
        return;
      }
    }

    // Fallback for non-dialog usage
    await handleSaveDraft();
  }, [isDialogMode, onSaveEmailQuote, buildDraftData, handleSaveDraft]);

  const handleExitEditing = useCallback(() => {
    stopEditingDraft();
  }, [stopEditingDraft]);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Email Quote Banner (Dialog Mode) */}
      {isEmailQuoteMode && isDialogMode && originalEmailQuote && (
        <div className="p-4 mb-4 bg-blue-50 border-l-4 border-blue-500 rounded">
          <h3 className="font-medium text-blue-900 mb-2">
            Editing Email Auto-Quote
          </h3>
          <div className="text-sm text-blue-800 space-y-1">
            <p>
              <strong>Original Price:</strong> £
              {(
                originalEmailQuote.originalPrice ||
                originalEmailQuote.price ||
                originalEmailQuote.totalPrice ||
                0
              ).toFixed(2)}
            </p>
            {selectedCustomer?.customer_name && (
              <p>
                <strong>Customer:</strong> {selectedCustomer.customer_name}
              </p>
            )}
            <p>
              <strong>Pricing Tier:</strong>{" "}
              {(pricingTier || "retail").charAt(0).toUpperCase() +
                (pricingTier || "retail").slice(1)}
            </p>
            <p className="text-xs text-blue-600 mt-2">
              Adjust the configuration below and click "Save Updated Quote" when
              ready.
            </p>
          </div>
        </div>
      )}

      {isLoadingQuote && (
        <div className="p-4 mb-4 bg-blue-100 text-blue-700 rounded">
          Loading quote settings...
        </div>
      )}

      {editingDraftId && (
        <div className="p-4 mb-4 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-medium">Editing draft</h3>
              <p className="text-sm text-emerald-800">
                Use "Update Draft" to overwrite the original draft, or "Save as Draft" to create a new one.
              </p>
            </div>
            <button
              type="button"
              onClick={handleExitEditing}
              className="text-sm px-3 py-2 border border-emerald-300 text-emerald-800 rounded hover:bg-emerald-100"
            >
              Exit Editing
            </button>
          </div>
        </div>
      )}

      <CustomPricingBanner
        selectedCustomer={selectedCustomer}
        onDismiss={() => {}}
      />

      {quoteLoadWarnings.length > 0 && (
        <div className="p-4 mb-4 bg-yellow-100 text-yellow-800 rounded">
          <h4 className="font-medium mb-2">Settings Updated</h4>
          <p className="text-sm mb-2">
            The quote has been loaded, but some items were updated:
          </p>
          <ul className="text-sm list-disc list-inside space-y-1">
            {quoteLoadWarnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
          <button
            onClick={() => setQuoteLoadWarnings([])}
            className="mt-2 text-xs text-yellow-600 underline hover:text-yellow-700"
          >
            Dismiss
          </button>
        </div>
      )}

      {error && (
        <div className="p-4 mb-4 bg-red-100 text-red-700 rounded">{error}</div>
      )}

      {isLoading ? (
        <div className="p-4 text-center">Loading pricing data...</div>
      ) : (
        <>
          <div className="bg-white px-6 pt-3 pb-6 rounded-lg shadow-md mb-24">
            <form>
              <UniversalPieceManager
                pieces={universalPieces}
                onPiecesChange={setUniversalPieces}
                jobTypes={jobTypes}
                getMaterialsForJobType={getMaterialsForJobType}
                getFinishesForJobType={getFinishesForJobType}
                allowMultipleMaterialsForJobType={
                  allowMultipleMaterialsForJobType
                }
                finishingMaterials={finishingMaterials}
                autoExpandKey={autoExpandKey}
                hideAddItemButton={isDialogMode}
              />

              {/* Temporarily hide optional product name input */}
              {/*
              <div className="mt-6">
                <label className="block text-sm font-medium mb-1">Product Name (Optional)</label>
                <input
                  type="text"
                  value={jobName}
                  onChange={(event) => setJobName(event.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Give this product a name (e.g. 'Entrance Banner')"
              />
              <p className="text-xs text-gray-500 mt-1">
                If left empty, a name will be generated based on the configured pieces.
              </p>
            </div>
            */}

              <p className="mt-3 text-xs text-gray-500">
                Prices are indicative and may change after artwork review.
              </p>

              <div className="mt-6">
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  className="w-full p-2 border rounded"
                  rows={3}
                  placeholder="Add any additional notes"
                />
              </div>

              {productAddSuccess && (
                <div className="mt-6 p-3 bg-green-100 text-green-800 rounded flex items-center justify-between">
                  <span>
                    {productAddSuccessCount > 1
                      ? `${productAddSuccessCount} items added to quote!`
                      : "Item added to quote!"}
                  </span>
                  <span className="text-sm text-green-600">
                    {currentQuote.items.length} item
                    {currentQuote.items.length !== 1 ? "s" : ""} in quote
                  </span>
                </div>
              )}
            </form>
          </div>

          {Object.keys(breakdown).length > 0 && !error && (
            <div className="mt-8 bg-white p-6 rounded-lg shadow-md mb-24">
              <h2 className="text-xl font-semibold mb-4">Price Breakdown</h2>
              {showDetailedBreakdown ? (
                <DetailedPriceBreakdown breakdown={breakdown} />
              ) : (
                <SimplePriceBreakdown breakdown={breakdown} />
              )}
            </div>
          )}
        </>
      )}

      {!isLoading && (
        <div className="fixed bottom-0 left-0 right-0 bg-white p-2 border-t border-gray-200 shadow-md z-10">
          {isDialogMode && isEmailQuoteMode ? (
            /* Dialog Mode Action Bar */
            <div className="max-w-4xl mx-auto flex flex-row justify-between items-center gap-2 sm:gap-4">
              <div className="font-bold text-lg sm:text-xl md:text-2xl truncate">
                Updated Price: £{calculatedPrice.toFixed(2)}
              </div>
              <div className="flex flex-nowrap whitespace-nowrap gap-1.5 sm:gap-2">
                <button
                  type="button"
                  onClick={() =>
                    dialogBridge?.closeDialog({
                      success: false,
                      action: "cancel",
                    })
                  }
                  className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-gray-200 text-gray-800 rounded"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveEmailQuote}
                  className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-blue-600 text-white rounded disabled:bg-blue-400"
                  disabled={calculatedPrice <= 0 || !!error}
                >
                  Save Updated Quote
                </button>
              </div>
            </div>
          ) : (
            /* Normal Mode Action Bar */
            <div className="max-w-4xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                {pricingTier === "partner" ||
                pricingTier === "choosyg" ||
                (pricingTier === undefined &&
                  (currentUser?.pricing_tier === "partner" ||
                    currentUser?.pricing_tier === "choosyg")) ? (
                  <div className="font-bold text-lg sm:text-xl md:text-2xl">
                    Price: £
                    {(breakdown?.partner_pricing_note &&
                    typeof breakdown?.retail_price === "number"
                      ? breakdown.retail_price
                      : calculatedPrice
                    ).toFixed(2)}
                    {typeof breakdown?.retail_subtotal_before_min_charge ===
                      "number" &&
                      !breakdown?.partner_pricing_note && (
                        <span className="hidden sm:inline text-xs sm:text-sm font-normal text-gray-600 ml-2">
                          (Retail: £
                          {breakdown.retail_subtotal_before_min_charge.toFixed(
                            2,
                          )}
                          )
                        </span>
                      )}
                  </div>
                ) : (
                  <div className="font-bold text-lg sm:text-xl md:text-2xl">
                    Total: £{calculatedPrice.toFixed(2)}
                  </div>
                )}
              </div>

              <div className="flex flex-nowrap whitespace-nowrap overflow-x-auto no-scrollbar gap-1.5 sm:gap-2 justify-end">
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-gray-200 text-gray-800 rounded"
                >
                  Reset
                </button>
                {editingDraftId && (
                  <button
                    type="button"
                    onClick={handleUpdateDraft}
                    className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-green-600 text-white rounded disabled:bg-green-400"
                    disabled={calculatedPrice <= 0 || !!error}
                  >
                    Update Draft
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-emerald-600 text-white rounded disabled:bg-emerald-400"
                  disabled={calculatedPrice <= 0 || !!error}
                >
                  {editingDraftId ? "Save as Draft" : "Save Draft"}
                </button>
                {editingDraftId && (
                  <button
                    type="button"
                    onClick={handleExitEditing}
                    className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-gray-200 text-gray-800 rounded"
                  >
                    Exit Editing
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleAddToQuote}
                  className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-blue-600 text-white rounded disabled:bg-blue-400"
                  disabled={calculatedPrice <= 0 || !!error}
                >
                  Add to Quote
                </button>
                <button
                  type="button"
                  onClick={openQuoteReview}
                  className="text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 whitespace-nowrap bg-indigo-600 text-white rounded disabled:bg-indigo-400 relative mr-3"
                  disabled={
                    currentQuote.items.length === 0 &&
                    (calculatedPrice <= 0 || !!error)
                  }
                >
                  <span>Review Quote</span>
                  {currentQuote.items.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-5 h-5 px-1 flex items-center justify-center">
                      {currentQuote.items.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {showQuoteReview &&
        (currentUser && (hasRole("manager") || hasRole("admin")) ? (
          <ManagerQuoteReview
            onClose={(result) => {
              setShowQuoteReview(false);
              if (
                result &&
                typeof result === "object" &&
                result.type === "order_submitted_to_printlogic"
              ) {
                setSuccessModalData({
                  orderNumber: result.orderNumber,
                  quoteName: result.quoteName,
                });
                setShowSuccessModal(true);
                resetForm();
              } else if (result && result === "add_more_items") {
                // Preserve customer selection when adding more items
                resetForm({ preserveCustomer: true });
              } else if (result) {
                resetForm();
              }
            }}
            preSelectedCustomer={selectedCustomer}
          />
        ) : (
          <QuoteReview
            onClose={(result) => {
              setShowQuoteReview(false);
              if (
                result &&
                typeof result === "object" &&
                result.type === "order_submitted_to_printlogic"
              ) {
                setSuccessModalData({
                  orderNumber: result.orderNumber,
                  quoteName: result.quoteName,
                });
                setShowSuccessModal(true);
                resetForm();
              } else if (result && result === "add_more_items") {
                // Preserve customer selection when adding more items
                resetForm({ preserveCustomer: true });
              } else if (result) {
                resetForm();
              }
            }}
          />
        ))}

      <PrintlogicSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        orderNumber={successModalData.orderNumber}
        quoteName={successModalData.quoteName}
      />
    </div>
  );
};

export default PriceCalculator;
